import { AssessmentTypes } from './assessments.types';
import isFYLI2017 from './validation/isFYLI2017';
import isFYLI2016 from './validation/isFLYI2016';
import isSYLI2015 from './validation/isSYLI2015';
import isCCMR2018 from './validation/isCCMR2018';
import isCCMR2019 from './validation/isCCMR2019';
import isFYLI2018 from './validation/isFYLI2018';
import isF5YLI2017 from './validation/isF5YLI2017';
import isSYLI2016 from './validation/isSYLI2016';
import isCsvGradData from './validation/isCsvGradData';
import isXlsxGradData from './validation/isXlsxGradData';
import isXlsxGradListing from './validation/isXlsxGradListing';
import isSTAAR2021Custom from './validation/isSTAAR2021Custom';
import isEOC2021Custom from './validation/isEOC2021Custom';
import isSAT2019 from './validation/isSAT2019';
import isACT from './validation/isACT';

import isSTAAR from './validation/isSTAAR';
import isTELPAS from './validation/isTELPAS';
import isEOC from './validation/isEOC';
import isCCMR from './validation/isCCMR';
import isCCMRCSV from './validation/isCCMRCSV';
import isXlsxCcmrVerifier from './validation/isXlsxCcmrVerifier';
import isWrongCcmrFile from './validation/isWrongCcmrFile';
import isCcmrOutcomesBonus from './validation/isCcmrOutcomesBonus';
import isAcceleratedTesters from './validation/isAcceleratedTesters';
import isIxlUsage from './validation/isIxlUsage';
import isFYLI from './validation/isFYLI';
import isF5YLI from './validation/isF5YLI';
import isSYLI from './validation/isSYLI';
import isSTAARCustom from './validation/isSTAARCustom';

import isTelpasComposite from './validation/isTelpasComposite';

import isComboAssessmentFile from './validation/isComboAssessmentFile';
import isInterim from './validation/isInterim';
import isDMAC from './validation/isDMAC';
import isEduphoriaAware from './validation/isEduphoriaAware';

const makeResult = (type, administrationCode = null) => ({ type, administrationCode });

export const determineFileType = async (file) => {
  // TELPAS Composite Scores (TEAL rollup CSV) — checked first because it is a very specific
  // header signature and must not be mistaken for any other CSV (CCMR/grad).
  const telpasCompositeResult = await isTelpasComposite(file);
  if (telpasCompositeResult.isValid == true) {
    return makeResult(AssessmentTypes.TELPASComposite);
  }

  // Combo
  if (await isComboAssessmentFile(file)) {
    return makeResult(AssessmentTypes.ComboAssessment);
  }

  // Interim
  if (await isInterim(file)) {
    return makeResult(AssessmentTypes.Interim);
  }
  if (await isDMAC(file)) {
    return makeResult(AssessmentTypes.DMACInterim);
  }
  // Eduphoria Aware xlsx export (unit tests / local assessments) — rides the Interim rail;
  // the server parses the workbook when the stored bytes are xlsx rather than the CSV template.
  if (await isEduphoriaAware(file)) {
    return makeResult(AssessmentTypes.Interim);
  }

  var currentYear = new Date().getFullYear();
  var firstPartOfYear = currentYear.toString().substring(0, 2);

  //Dynamic SAT (STAAR - STAARALT-EOC-EOCALT-TELPAS-TELPASALT)
  var validationResult = await isSTAAR(file);
  if (validationResult.isValid == true) {
    var assesmentType = AssessmentTypes.STAAR + firstPartOfYear + validationResult.assessmentYear;
    return makeResult(assesmentType);
  }
  validationResult = await isSTAAR(file, true);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.STAARALT + firstPartOfYear + validationResult.assessmentYear);
  }
  validationResult = await isTELPAS(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.TELPAS + firstPartOfYear + validationResult.assessmentYear);
  }

  validationResult = await isTELPAS(file, true);
  if (validationResult.isValid == true) {
    var assesmentType = AssessmentTypes.TELPASALT + firstPartOfYear + validationResult.assessmentYear;
    return makeResult(assesmentType);
  }
  validationResult = await isEOC(file);
  if (validationResult.isValid == true) {
    return makeResult(
      AssessmentTypes.EOC + firstPartOfYear + validationResult.assessmentYear,
      validationResult.administrationCode
    );
  }
  validationResult = await isEOC(file, true);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.EOCALT + firstPartOfYear + validationResult.assessmentYear);
  }

  validationResult = await isSTAARCustom(file);

  if (validationResult.isValid == true) {
    let staarCustomYear = currentYear;
    const yearRegex = /\d{4}/; // Matches a sequence of 4 digits
    const match = file.name.match(yearRegex);
    if (match) {
      staarCustomYear = match[0];
    }
    return makeResult(AssessmentTypes.STAARCustom + staarCustomYear);
  }

  //Dynamic CCMR
  validationResult = await isCCMR(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.CCMR + validationResult.assessmentYear);
  }

  validationResult = await isCCMRCSV(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.CCMRCSV + currentYear);
  }

  validationResult = await isXlsxCcmrVerifier(file); // TEA CCMR Verifier workbook, 2026 accountability and later
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.CCMRCSV + currentYear);
  }

  // CCMR Outcomes Bonus (HB3 funding report) — its own assessment type, fully independent of the
  // A-F CCMR pipeline. Detected BEFORE isWrongCcmrFile so a genuine OB file routes to its own type
  // rather than being flagged as a wrong-CCMR upload.
  validationResult = await isCcmrOutcomesBonus(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.CCMROutcomesBonus + validationResult.assessmentYear);
  }

  // Catch the two CCMR exports that are easily mistaken for the Verifier (CCMR Tracker, CCMR
  // Outcomes Bonus) and surface a specific "use the Verifier" message instead of letting them
  // fall through to a generic "unrecognized file" or fail server-side. Runs before the grad/other
  // checks so a Tracker can't accidentally match a later validator.
  const wrongCcmr = await isWrongCcmrFile(file);
  if (wrongCcmr.isWrong) {
    return { type: null, error: wrongCcmr.message };
  }

  // TEA Accelerated Testers Final listing — requires the `Final` filename token plus the exact 12-col
  // header; year is read from the filename (validator rejects when absent). Type string carries the
  // 4-digit accountability year so the backend dynamic switch routes it.
  validationResult = await isAcceleratedTesters(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.AcceleratedTesters + validationResult.year);
  }

  // IXL Analytics usage export (HB 1416 supplemental-instruction hours). Conservative
  // detector: keys only on IXL-unique header tokens (SmartScore / Skills practiced, or a
  // name column plus an IXL time column) so it cannot misroute a STAAR/EOC/roster/TELPAS/
  // CCMR upload. Type string carries the 4-digit accountability year (e.g. IxlUsage2026);
  // the backend snapshotYear is that year minus one.
  validationResult = await isIxlUsage(file);
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.IxlUsage + validationResult.year);
  }

  //Dynamic Grade Data (FYLI-F5YLI-SYLI)
  const xlsxGradTypes = { FOUR: AssessmentTypes.FYLI, FIVE: AssessmentTypes.F5YLI, SIX: AssessmentTypes.SYLI };
  validationResult = await isXlsxGradData(file); // TEA xlsx summary reports, Class of 2025 and later
  if (validationResult.isValid == true) {
    return makeResult(xlsxGradTypes[validationResult.reportLength] + validationResult.assessmentYear);
  }

  validationResult = await isXlsxGradListing(file); // TEA xlsx per-student listing (aggregates to the summary)
  if (validationResult.isValid == true) {
    return makeResult(xlsxGradTypes[validationResult.reportLength] + validationResult.assessmentYear);
  }

  validationResult = await isFYLI(file); // Previous Grade Year 4
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.FYLI + validationResult.assessmentYear);
  }

  validationResult = await isF5YLI(file); // Previous Grade Year 5
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.F5YLI + validationResult.assessmentYear);
  }

  validationResult = await isSYLI(file); // Previous Grade Year 6
  if (validationResult.isValid == true) {
    return makeResult(AssessmentTypes.SYLI + validationResult.assessmentYear);
  }
  /////////////////////////////2021 Custom///////////////////////////////////////////////
  if (await isSTAAR2021Custom(file)) {
    return makeResult(AssessmentTypes.STAAR2021Custom);
  }
  if (await isEOC2021Custom(file)) {
    return makeResult(AssessmentTypes.EOC2021Custom);
  }
  ////////////////////////////////////////2019//////////////////////////////////

  if (await isFYLI2017(file)) {
    return makeResult(AssessmentTypes.FYLI2017);
  }
  if (await isFYLI2016(file)) {
    return makeResult(AssessmentTypes.FYLI2016);
  }
  if (await isSYLI2015(file)) {
    return makeResult(AssessmentTypes.SYLI2015);
  }
  if (await isFYLI2018(file)) {
    return makeResult(AssessmentTypes.FYLI2018);
  }
  if (await isF5YLI2017(file)) {
    return makeResult(AssessmentTypes.F5YLI2017);
  }
  if (await isSYLI2016(file)) {
    return makeResult(AssessmentTypes.SYLI2016);
  }
  if (await isCCMR2019(file)) {
    return makeResult(AssessmentTypes.CCMR2019);
  }
  if (await isCCMR2018(file)) {
    return makeResult(AssessmentTypes.CCMR2018);
  }
  if (await isSAT2019(file)) {
    return makeResult(AssessmentTypes.SAT2019);
  }
  if (await isACT(file)) {
    return makeResult(AssessmentTypes.ACT);
  }

  const results = await isCsvGradData(file);
  if (results) {
    switch (`${results.year}:${results.type}`) {
      case '2016:SIX-YEAR':
        return makeResult(AssessmentTypes.SYLI2016);
      case '2017:FIVE-YEAR':
        return makeResult(AssessmentTypes.F5YLI2017);
      case '2018:FOUR-YEAR':
        return makeResult(AssessmentTypes.FYLI2018);
      case '2015:SIX-YEAR':
        return makeResult(AssessmentTypes.SYLI2015);
      case '2016:FIVE-YEAR':
        return makeResult(AssessmentTypes.FYLI2016);
      case '2017:FOUR-YEAR':
        return makeResult(AssessmentTypes.FYLI2017);
      default:
        return null;
    }
  }
  return null;
};

export default 'assessments.validation2.js';
